import { ApolloLink, Observable } from '@apollo/client'
import { print } from 'graphql'

const GRAPHQL_URI = import.meta.env.VITE_GRAPHQL_URL ?? '/graphql'

const isFileValue = (value: unknown): value is Blob =>
  typeof Blob !== 'undefined' && value instanceof Blob

export const variablesHaveFiles = (value: unknown): boolean => {
  if (isFileValue(value)) {
    return true
  }

  if (Array.isArray(value)) {
    return value.some(variablesHaveFiles)
  }

  if (value !== null && typeof value === 'object') {
    return Object.values(value).some(variablesHaveFiles)
  }

  return false
}

type FileMap = Map<Blob, string[]>

const extractFiles = (value: unknown, path: string, files: FileMap): unknown => {
  if (isFileValue(value)) {
    const existing = files.get(value)

    if (existing) {
      existing.push(path)
    } else {
      files.set(value, [path])
    }

    return null
  }

  if (Array.isArray(value)) {
    return value.map((item, index) => extractFiles(item, `${path}.${index}`, files))
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        extractFiles(item, `${path}.${key}`, files),
      ]),
    )
  }

  return value
}

/**
 * Terminating link that sends operations as a `multipart/form-data` request
 * following the GraphQL multipart request spec, so `Upload` scalars work.
 * Use it only for operations whose variables contain a File/Blob.
 */
export const uploadLink = new ApolloLink(operation => {
  return new Observable<ApolloLink.Result>(observer => {
    const files: FileMap = new Map()
    const variables = extractFiles(operation.variables, 'variables', files)
    const body = new FormData()

    body.append(
      'operations',
      JSON.stringify({
        query: print(operation.query),
        variables,
        operationName: operation.operationName,
      }),
    )

    const fileMap: Record<string, string[]> = {}
    const orderedFiles: Blob[] = []

    files.forEach((paths, file) => {
      fileMap[orderedFiles.length] = paths
      orderedFiles.push(file)
    })

    body.append('map', JSON.stringify(fileMap))
    orderedFiles.forEach((file, index) => body.append(String(index), file))

    const headers = (operation.getContext().headers ?? {}) as Record<string, string>
    const controller = new AbortController()

    fetch(GRAPHQL_URI, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'apollo-require-preflight': 'true',
        ...headers,
      },
      body,
      signal: controller.signal,
    })
      .then(response => response.json())
      .then((result: ApolloLink.Result) => {
        observer.next(result)
        observer.complete()
      })
      .catch((error: Error) => {
        if (!controller.signal.aborted) {
          observer.error(error)
        }
      })

    return () => controller.abort()
  })
})
